import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Tv2, Film, Compass, ArrowLeft, Loader2, Youtube, Sparkles,
  Search, Wand2, TrendingUp, TrendingDown, Minus, Check, ChevronDown, ChevronRight,
  Flame, ExternalLink, Send, ListChecks, Star, FileDown, Bookmark, BookmarkCheck, Trash2, Tv2 as TvIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { downloadNichesPdf } from "@/lib/nichePdf";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#00e5ff";

function scoreColor(v) {
  if (v == null) return "#6b7280";
  if (v >= 75) return "#22c55e";
  if (v >= 55) return "#f5b301";
  if (v >= 35) return "#fb923c";
  return "#ef4444";
}

function TrendPill({ dir }) {
  const d = (dir || "").toLowerCase();
  const map = {
    rising: { icon: TrendingUp, color: "#22c55e", label: "Rising" },
    stable: { icon: Minus, color: "#f5b301", label: "Stable" },
    declining: { icon: TrendingDown, color: "#ef4444", label: "Declining" },
  };
  const cfg = map[d] || map.stable;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
      style={{ borderColor: `${cfg.color}55`, color: cfg.color, backgroundColor: `${cfg.color}14` }}
      data-testid="niche-trend-pill"
    >
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function ScoreRing({ value, size = 64 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const off = c - (v / 100) * c;
  const color = scoreColor(v);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#272a35" strokeWidth="5" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        className="font-display font-bold" fill={color} fontSize={size * 0.3}>
        {Math.round(v)}
      </text>
    </svg>
  );
}

function ScoreBar({ label, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="flex items-center gap-2" data-testid={`score-bar-${label}`}>
      <span className="w-24 shrink-0 text-[11px] uppercase tracking-wide text-[#9ca3af]">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#1d2028]">
        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, backgroundColor: scoreColor(v) }} />
      </div>
      <span className="w-7 shrink-0 text-right text-xs font-semibold" style={{ color: scoreColor(v) }}>{v}</span>
    </div>
  );
}

const SCORE_ROWS = [
  ["demand", "Demand"],
  ["competition", "Competition"],
  ["breakout", "Breakout"],
  ["trend", "Trend"],
  ["idea_capacity", "Idea capacity"],
  ["shorts_fit", "Shorts fit"],
  ["longform_fit", "Long-form fit"],
  ["monetization", "Monetization"],
  ["difficulty", "Ease of production"],
  ["copyright_risk", "Copyright safety"],
];

function NicheCard({ niche, index, expanded, onToggle, selectedForCompare, onCompareToggle, format, onSend, onDownload,
  mode = "research", channels = [], onSave, onRemove }) {
  const scores = niche.scores || {};
  return (
    <div
      className="rounded-lg border bg-[#0e1015] transition-colors"
      style={{ borderColor: expanded ? `${ACCENT}55` : "#272a35" }}
      data-testid={`niche-card-${index}`}
    >
      {/* Header */}
      <div className="flex items-start gap-4 p-5">
        <ScoreRing value={niche.opportunity_score} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-bold tracking-tight text-[#f3f4f6]" data-testid={`niche-name-${index}`}>
              {niche.name}
            </h3>
            <TrendPill dir={niche.trend_direction} />
            {niche.opportunity_score >= 75 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#22c55e]/15 px-2 py-0.5 text-[11px] font-medium text-[#22c55e]">
                <Flame className="h-3 w-3" /> High opportunity
              </span>
            )}
            {mode === "saved" && niche.channel_name && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#00e5ff]/40 bg-[#00e5ff]/10 px-2 py-0.5 text-[11px] font-medium text-[#00e5ff]" data-testid={`niche-saved-channel-${index}`}>
                <TvIcon className="h-3 w-3" /> {niche.channel_name}
              </span>
            )}
          </div>
          {niche.demand_note && <p className="mt-1 text-xs text-[#9ca3af]">{niche.demand_note}</p>}
          {niche.why_recommended && <p className="mt-2 text-sm leading-relaxed text-[#d1d5db]">{niche.why_recommended}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              data-testid={`niche-expand-${index}`}
              onClick={onToggle}
              variant="outline"
              className="h-8 rounded-md border-[#272a35] bg-transparent text-xs text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              {expanded ? <ChevronDown className="mr-1 h-3.5 w-3.5" /> : <ChevronRight className="mr-1 h-3.5 w-3.5" />}
              {expanded ? "Hide details" : "View details"}
            </Button>
            {mode === "research" && (
              <Button
                size="sm"
                data-testid={`niche-compare-${index}`}
                onClick={onCompareToggle}
                variant="outline"
                className="h-8 rounded-md border-[#272a35] bg-transparent text-xs hover:bg-[#1d2028]"
                style={selectedForCompare ? { borderColor: `${ACCENT}88`, color: ACCENT } : { color: "#f3f4f6" }}
              >
                <ListChecks className="mr-1 h-3.5 w-3.5" />
                {selectedForCompare ? "Comparing" : "Compare"}
              </Button>
            )}
            <Button
              size="sm"
              data-testid={`niche-send-studio-${index}`}
              onClick={() => onSend(niche.name)}
              className="h-8 rounded-md bg-[#00e5ff] text-xs font-semibold text-black hover:bg-[#33ebff]"
            >
              <Send className="mr-1 h-3.5 w-3.5" /> Start story ({format === "short" ? "Shorts" : "Long"})
            </Button>
            {mode === "research" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    data-testid={`niche-save-${index}`}
                    variant="outline"
                    className="h-8 rounded-md border-[#272a35] bg-transparent text-xs text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
                    title="Save this niche to a channel"
                  >
                    <Bookmark className="mr-1 h-3.5 w-3.5" /> Save
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto border-[#272a35] bg-[#14161c] text-[#f3f4f6]" data-testid={`niche-save-menu-${index}`}>
                  <DropdownMenuLabel className="text-[#9ca3af]">Save to channel</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#272a35]" />
                  {channels.length === 0 ? (
                    <DropdownMenuItem disabled className="text-[#6b7280]">No channels yet — create one first</DropdownMenuItem>
                  ) : (
                    channels.map((c) => (
                      <DropdownMenuItem
                        key={c.channel_id}
                        data-testid={`niche-save-to-${index}-${c.channel_id}`}
                        onClick={() => onSave(niche, c.channel_id)}
                        className="cursor-pointer focus:bg-[#1d2028] focus:text-[#f3f4f6]"
                      >
                        <TvIcon className="mr-2 h-3.5 w-3.5 text-[#00e5ff]" /> {c.name || "Untitled channel"}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {mode === "saved" && (
              <Button
                size="sm"
                data-testid={`niche-remove-${index}`}
                onClick={() => onRemove(niche)}
                variant="outline"
                className="h-8 rounded-md border-[#272a35] bg-transparent text-xs text-[#f3f4f6] hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-400"
                title="Remove this saved niche"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            )}
            <Button
              size="sm"
              data-testid={`niche-download-${index}`}
              onClick={() => onDownload(niche)}
              variant="outline"
              className="h-8 rounded-md border-[#272a35] bg-transparent text-xs text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
              title="Download this niche as a PDF"
            >
              <FileDown className="mr-1 h-3.5 w-3.5" /> PDF
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="space-y-6 border-t border-[#272a35] p-5" data-testid={`niche-details-${index}`}>
          {/* Scores grid */}
          <div className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2">
            {SCORE_ROWS.map(([key, label]) => (
              <ScoreBar key={key} label={label} value={scores[key]} />
            ))}
          </div>

          {/* Formats */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {niche.faceless_style && (
              <div className="rounded-md border border-[#272a35] bg-[#14161c] p-3">
                <p className="overline mb-1">Faceless style</p>
                <p className="text-sm text-[#d1d5db]">{niche.faceless_style}</p>
              </div>
            )}
            {niche.shorts_format && (
              <div className="rounded-md border border-[#272a35] bg-[#14161c] p-3">
                <p className="overline mb-1">Shorts format</p>
                <p className="text-sm text-[#d1d5db]">{niche.shorts_format}</p>
              </div>
            )}
            {niche.longform_format && (
              <div className="rounded-md border border-[#272a35] bg-[#14161c] p-3">
                <p className="overline mb-1">Long-form format</p>
                <p className="text-sm text-[#d1d5db]">{niche.longform_format}</p>
              </div>
            )}
          </div>

          {/* Series */}
          {Array.isArray(niche.series) && niche.series.length > 0 && (
            <div>
              <p className="overline mb-2">10 repeatable series</p>
              <div className="flex flex-wrap gap-2">
                {niche.series.map((s, i) => (
                  <span key={i} className="rounded-full border border-[#272a35] bg-[#14161c] px-3 py-1 text-xs text-[#d1d5db]" data-testid={`niche-${index}-series-${i}`}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sample titles */}
          {Array.isArray(niche.sample_titles) && niche.sample_titles.length > 0 && (
            <div>
              <p className="overline mb-2">Sample high-CTR titles</p>
              <div className="space-y-1.5">
                {niche.sample_titles.map((t, i) => (
                  <div key={i} className="group flex items-center justify-between gap-3 rounded-md border border-[#272a35] bg-[#14161c] px-3 py-2" data-testid={`niche-${index}-title-${i}`}>
                    <span className="min-w-0 flex-1 truncate text-sm text-[#f3f4f6]">{t}</span>
                    <button
                      onClick={() => onSend(t)}
                      title="Start a story from this title"
                      data-testid={`niche-${index}-title-send-${i}`}
                      className="shrink-0 text-[#6b7280] transition-colors hover:text-[#00e5ff]"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video ideas */}
          {Array.isArray(niche.video_ideas) && niche.video_ideas.length > 0 && (
            <div>
              <p className="overline mb-2">{niche.video_ideas.length} starter video ideas</p>
              <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
                {niche.video_ideas.map((v, i) => (
                  <div key={i} className="group flex items-center justify-between gap-2 rounded-md border border-[#272a35] bg-[#14161c] px-3 py-2" data-testid={`niche-${index}-idea-${i}`}>
                    <span className="min-w-0 flex-1 text-sm text-[#d1d5db]"><span className="mr-1 text-[#6b7280]">{i + 1}.</span>{v}</span>
                    <button
                      onClick={() => onSend(v)}
                      title="Start a story from this idea"
                      data-testid={`niche-${index}-idea-send-${i}`}
                      className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 text-[#6b7280] hover:text-[#00e5ff]"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Example channels */}
          {Array.isArray(niche.example_channels) && niche.example_channels.length > 0 && (
            <div>
              <p className="overline mb-2">Successful example channels</p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {niche.example_channels.map((c, i) => (
                  <div key={i} className="rounded-md border border-[#272a35] bg-[#14161c] p-3" data-testid={`niche-${index}-channel-${i}`}>
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-[#f3f4f6]">
                      <Youtube className="h-3.5 w-3.5 text-[#ff0033]" /> {c.name}
                    </p>
                    {c.why && <p className="mt-1 text-xs text-[#9ca3af]">{c.why}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompareTable({ niches, onClear }) {
  if (niches.length < 2) return null;
  return (
    <div className="mb-6 overflow-x-auto rounded-lg border border-[#00e5ff]/30 bg-[#0e1015] p-4" data-testid="niche-compare-table">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-[#f3f4f6]">
          <ListChecks className="h-4 w-4 text-[#00e5ff]" /> Comparing {niches.length} niches
        </p>
        <button onClick={onClear} className="text-xs text-[#9ca3af] hover:text-[#f3f4f6]" data-testid="niche-compare-clear">Clear</button>
      </div>
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr>
            <th className="p-2 text-left text-[11px] uppercase tracking-wide text-[#6b7280]">Metric</th>
            {niches.map((n, i) => (
              <th key={i} className="p-2 text-left font-display text-sm font-bold text-[#f3f4f6]">{n.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-[#272a35]">
            <td className="p-2 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">Opportunity</td>
            {niches.map((n, i) => (
              <td key={i} className="p-2">
                <span className="font-display text-lg font-bold" style={{ color: scoreColor(n.opportunity_score) }}>{n.opportunity_score}</span>
              </td>
            ))}
          </tr>
          {SCORE_ROWS.map(([key, label]) => (
            <tr key={key} className="border-t border-[#272a35]">
              <td className="p-2 text-[11px] uppercase tracking-wide text-[#9ca3af]">{label}</td>
              {niches.map((n, i) => (
                <td key={i} className="p-2 font-semibold" style={{ color: scoreColor((n.scores || {})[key]) }}>
                  {(n.scores || {})[key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-[#272a35]">
            <td className="p-2 text-[11px] uppercase tracking-wide text-[#9ca3af]">Trend</td>
            {niches.map((n, i) => (<td key={i} className="p-2"><TrendPill dir={n.trend_direction} /></td>))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function Niches() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [niches, setNiches] = useState([]);
  const [ran, setRan] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [compareIds, setCompareIds] = useState([]);
  const [format, setFormat] = useState("long");
  const [lastTopic, setLastTopic] = useState("");
  const [view, setView] = useState("research");
  const [channels, setChannels] = useState([]);
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedExpanded, setSavedExpanded] = useState({});

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/channels");
      setChannels(data || []);
    } catch { /* channels optional */ }
  }, []);
  useEffect(() => { loadChannels(); }, [loadChannels]);

  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const { data } = await api.get("/saved-niches");
      setSaved(data || []);
    } catch {
      toast.error("Could not load saved niches");
    } finally {
      setSavedLoading(false);
    }
  }, []);
  useEffect(() => { if (view === "saved") loadSaved(); }, [view, loadSaved]);

  const saveNiche = async (niche, channelId) => {
    try {
      const { niche_id, ...rest } = niche; // don't resend an existing niche_id
      await api.post(`/channels/${channelId}/niches`, { niche: rest });
      const ch = channels.find((c) => c.channel_id === channelId);
      toast.success(`Saved to "${ch?.name || "channel"}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save niche");
    }
  };

  const removeNiche = async (niche) => {
    try {
      await api.delete(`/channels/${niche.channel_id}/niches/${niche.niche_id}`);
      setSaved((s) => s.filter((n) => n.niche_id !== niche.niche_id));
      toast.success("Removed saved niche");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove niche");
    }
  };

  const research = async (findForMe) => {
    setLoading(true);
    setRan(true);
    setNiches([]);
    setExpanded({});
    setCompareIds([]);
    try {
      const { data } = await api.post("/niche/research", {
        topic: findForMe ? "" : topic.trim(),
        find_for_me: !!findForMe,
        count,
      });
      const jobId = data.job_id;
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let result = null;
      for (let i = 0; i < 100; i++) { // ~5 min at 3s
        await sleep(3000);
        try {
          const res = await api.get(`/text-job/${jobId}`);
          if (res.data.status === "done") { result = res.data.result; break; }
          if (res.data.status === "error") throw new Error(res.data.error || "Niche research failed");
        } catch (e) {
          if (e?.message && !e?.response) throw e; // real job error
          // transient poll error — keep polling
        }
      }
      if (!result) throw new Error("Niche research timed out. Please try again.");
      const list = result.niches || [];
      setNiches(list);
      setLastTopic(findForMe ? "" : topic.trim());
      if (list.length) setExpanded({ 0: true });
      if (!list.length) toast.info("No niches came back — try a different topic.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Niche research failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));

  const toggleCompare = (i) => {
    setCompareIds((ids) => {
      if (ids.includes(i)) return ids.filter((x) => x !== i);
      if (ids.length >= 5) { toast.info("You can compare up to 5 niches"); return ids; }
      return [...ids, i];
    });
  };

  const sendToStudio = (ideaText) => {
    navigate("/studio", { state: { newIdea: ideaText, newFormat: format } });
    toast.success("Sent to Studio");
  };

  const compareNiches = compareIds.map((i) => niches[i]).filter(Boolean);

  const downloadAll = () => {
    if (!niches.length) return;
    downloadNichesPdf(niches, { topic: lastTopic });
    toast.success("PDF downloaded");
  };
  const downloadOne = (niche) => {
    downloadNichesPdf([niche], { topic: niche.name });
    toast.success("PDF downloaded");
  };

  const NavBtn = ({ icon: Icon, label, testid, onClick, active }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
        active ? "bg-[#1d2028] text-[#f3f4f6] border border-[#00e5ff]/40" : "text-[#9ca3af] hover:bg-[#14161c] hover:text-[#f3f4f6] border border-transparent"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="grain flex h-screen overflow-hidden bg-[#0b0c10] text-[#f3f4f6]">
      {/* Left rail */}
      <aside className="flex h-full w-72 shrink-0 flex-col border-r border-[#272a35] bg-[#0e1015]">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-[#272a35]">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-white">
            <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
          </div>
          <span className="font-display font-bold tracking-tight">YouTube Factory <span className="text-[#00e5ff]">AI</span></span>
        </div>
        <div className="p-3 space-y-1">
          <NavBtn icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" onClick={() => navigate("/dashboard")} />
          <NavBtn icon={Compass} label="Niches" testid="nav-niches" active onClick={() => {}} />
          <NavBtn icon={Tv2} label="Channels" testid="nav-channels" onClick={() => navigate("/channels")} />
          <NavBtn icon={Film} label="Studio" testid="nav-studio" onClick={() => navigate("/studio")} />
        </div>
        <div className="mt-2 flex-1 overflow-y-auto px-4">
          <p className="overline">About</p>
          <p className="mt-3 text-xs leading-relaxed text-[#9ca3af]">
            Find high-opportunity niches for faceless English channels running both Shorts and
            3–10 minute videos. Scored to favor niches where small channels punch far above their subscriber count.
          </p>
        </div>
        <div className="border-t border-[#272a35] p-4">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1d2028] text-[#00e5ff]"><Youtube className="h-4 w-4" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "Signed in"}</p>
              <p className="truncate text-xs text-[#9ca3af]">{user?.email || ""}</p>
            </div>
            <button onClick={logout} title="Sign out" className="text-[#9ca3af] hover:text-red-400 transition-colors">
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-[#272a35] bg-black/60 px-6 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <div>
              <p className="overline !text-white">Research</p>
              <h1 className="font-display text-base font-semibold tracking-tight leading-none mt-0.5">Niche Research</h1>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-[#272a35] bg-[#14161c] p-0.5" data-testid="niche-view-tabs">
              <button
                data-testid="niche-view-research"
                onClick={() => setView("research")}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${view === "research" ? "bg-[#1d2028] text-[#f3f4f6]" : "text-[#9ca3af] hover:text-[#f3f4f6]"}`}
              >
                Research
              </button>
              <button
                data-testid="niche-view-saved"
                onClick={() => setView("saved")}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${view === "saved" ? "bg-[#1d2028] text-[#f3f4f6]" : "text-[#9ca3af] hover:text-[#f3f4f6]"}`}
              >
                Saved Niches{saved.length ? ` (${saved.length})` : ""}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-[#272a35] bg-[#14161c] p-0.5" data-testid="niche-format-toggle">
            <button
              data-testid="niche-format-long"
              onClick={() => setFormat("long")}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${format === "long" ? "bg-[#00e5ff] text-black" : "text-[#9ca3af] hover:text-[#f3f4f6]"}`}
            >
              Long-form
            </button>
            <button
              data-testid="niche-format-short"
              onClick={() => setFormat("short")}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${format === "short" ? "bg-[#00e5ff] text-black" : "text-[#9ca3af] hover:text-[#f3f4f6]"}`}
            >
              Shorts
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-4xl">
            {view === "research" ? (
            <>
            {/* Search controls */}
            <div className="rounded-lg border border-[#272a35] bg-[#0e1015] p-5" data-testid="niche-search-panel">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7280]" />
                  <Input
                    data-testid="niche-topic-input"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && topic.trim() && !loading) research(false); }}
                    placeholder="Enter a topic, e.g. personal finance, space, stoicism…"
                    className="border-[#272a35] bg-[#14161c] pl-9 text-[#f3f4f6] placeholder:text-[#6b7280]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#9ca3af]">Show</span>
                  <select
                    data-testid="niche-count-select"
                    value={count}
                    onChange={(e) => setCount(Number(e.target.value))}
                    className="rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6] outline-none"
                  >
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button
                  data-testid="niche-research-btn"
                  onClick={() => research(false)}
                  disabled={loading || !topic.trim()}
                  className="flex-1 rounded-md bg-[#00e5ff] font-semibold text-black hover:bg-[#33ebff] disabled:opacity-50"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Research this topic
                </Button>
                <Button
                  data-testid="niche-find-btn"
                  onClick={() => research(true)}
                  disabled={loading}
                  variant="outline"
                  className="flex-1 rounded-md border-[#00e5ff]/40 bg-transparent font-semibold text-[#00e5ff] hover:bg-[#00e5ff]/10 hover:text-[#00e5ff]"
                >
                  <Wand2 className="mr-2 h-4 w-4" /> Find a niche for me
                </Button>
              </div>
            </div>

            {/* Results */}
            <div className="mt-6">
              {loading ? (
                <div className="flex flex-col items-center gap-3 py-20" data-testid="niche-loading">
                  <Loader2 className="h-7 w-7 animate-spin text-[#00e5ff]" />
                  <p className="text-sm text-[#9ca3af]">Analyzing demand, competition & breakout potential…</p>
                </div>
              ) : !ran ? (
                <div className="mx-auto max-w-md rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-10 text-center" data-testid="niche-empty">
                  <Compass className="mx-auto h-10 w-10 text-[#00e5ff]" />
                  <h2 className="mt-4 font-display text-lg font-bold tracking-tight">Find your next niche</h2>
                  <p className="mt-2 text-sm text-[#9ca3af]">
                    Enter a topic or let us find high-opportunity, low-competition niches for a brand-new faceless channel.
                  </p>
                </div>
              ) : niches.length === 0 ? (
                <div className="mx-auto max-w-md rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-10 text-center" data-testid="niche-noresults">
                  <p className="text-sm text-[#9ca3af]">No niches came back. Try a broader topic or "Find a niche for me".</p>
                </div>
              ) : (
                <>
                  <CompareTable niches={compareNiches} onClear={() => setCompareIds([])} />
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs text-[#9ca3af]">
                      <Star className="h-3.5 w-3.5 text-[#f5b301]" />
                      Ranked by YTFactory Opportunity Score — higher favors small-channel breakout, demand & safety.
                    </div>
                    <Button
                      size="sm"
                      data-testid="niche-download-all-btn"
                      onClick={downloadAll}
                      variant="outline"
                      className="h-8 shrink-0 rounded-md border-[#272a35] bg-transparent text-xs text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
                    >
                      <FileDown className="mr-1.5 h-3.5 w-3.5" /> Download PDF
                    </Button>
                  </div>
                  <div className="space-y-4" data-testid="niche-results">
                    {niches.map((n, i) => (
                      <NicheCard
                        key={i}
                        niche={n}
                        index={i}
                        format={format}
                        mode="research"
                        channels={channels}
                        onSave={saveNiche}
                        expanded={!!expanded[i]}
                        onToggle={() => toggleExpand(i)}
                        selectedForCompare={compareIds.includes(i)}
                        onCompareToggle={() => toggleCompare(i)}
                        onSend={sendToStudio}
                        onDownload={downloadOne}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            </>
            ) : (
              <div data-testid="saved-niches-view">
                {savedLoading ? (
                  <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#00e5ff]" /></div>
                ) : saved.length === 0 ? (
                  <div className="mx-auto max-w-md rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-10 text-center" data-testid="saved-niches-empty">
                    <BookmarkCheck className="mx-auto h-10 w-10 text-[#00e5ff]" />
                    <h2 className="mt-4 font-display text-lg font-bold tracking-tight">No saved niches yet</h2>
                    <p className="mt-2 text-sm text-[#9ca3af]">
                      Research a niche, then hit <span className="text-[#f3f4f6]">Save</span> to keep its series, titles and ideas one click away here — organized by channel.
                    </p>
                    <Button
                      data-testid="saved-goto-research"
                      onClick={() => setView("research")}
                      className="mt-5 rounded-md bg-[#00e5ff] font-semibold text-black hover:bg-[#33ebff]"
                    >
                      <Search className="mr-2 h-4 w-4" /> Research a niche
                    </Button>
                  </div>
                ) : (
                  (() => {
                    const byChannel = {};
                    saved.forEach((n) => {
                      const k = n.channel_id || "__none__";
                      (byChannel[k] || (byChannel[k] = { name: n.channel_name, items: [] })).items.push(n);
                    });
                    return Object.entries(byChannel).map(([cid, grp]) => (
                      <div key={cid} className="mb-8" data-testid={`saved-group-${cid}`}>
                        <div className="mb-3 flex items-center gap-2">
                          <Tv2 className="h-4 w-4 text-[#00e5ff]" />
                          <h2 className="font-display text-sm font-bold tracking-tight text-[#f3f4f6]">{grp.name || "Untitled channel"}</h2>
                          <span className="text-xs text-[#6b7280]">{grp.items.length} niche{grp.items.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="space-y-4">
                          {grp.items.map((n) => (
                            <NicheCard
                              key={n.niche_id}
                              niche={n}
                              index={n.niche_id}
                              format={format}
                              mode="saved"
                              expanded={!!savedExpanded[n.niche_id]}
                              onToggle={() => setSavedExpanded((e) => ({ ...e, [n.niche_id]: !e[n.niche_id] }))}
                              onSend={sendToStudio}
                              onDownload={downloadOne}
                              onRemove={removeNiche}
                            />
                          ))}
                        </div>
                      </div>
                    ));
                  })()
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
